import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, throwError, take, filter, switchMap, Subject, map } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { Authentication, AuthenticationForm, Client, Address, ClientUserForm, User } from '../../models';
import { IResponseSuccess, IResponseObjectSuccess, IResponse } from '@core/interfaces';
import { transformAuthUpdate, transformToClientUpdate } from '@core/helpers';
import { Store } from '@ngrx/store';
import { selectAuth, selectStore } from '@store/selectors';
import { Store as StoreModel, Status } from '@core/models';
import { AUTH_FIND_SUCCESS, CLIENT_CLEAR_STATE, USER_CLEAR_STATE } from '@/store/actions';

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private readonly TOKEN_KEY = 'access-token';
	private readonly USER_KEY = 'user_data';
	private readonly TOKEN_EXPIRY_KEY = 'token_expiry';
	private readonly TOKEN_DURATION_HOURS = 1;
	private idLocal: string = '';
	private isLoggedInSubject = new BehaviorSubject<boolean>(this.isAuthenticated());
	public isLoggedIn$ = this.isLoggedInSubject.asObservable();

	store$: Observable<StoreModel | null>;
	auth$: Observable<Authentication | null>;

	private closeDialogSource = new Subject<void>();
	closeDialog$ = this.closeDialogSource.asObservable();

	private updateCompletedSource = new Subject<void>();
	updateCompleted$ = this.updateCompletedSource.asObservable();

	constructor(
		private http: HttpClient,
		private cookieService: CookieService,
		private store: Store
	) {
		this.store$ = this.store.select(selectStore);
		this.auth$ = this.store.select(selectAuth);
	}

	closeDialog(): void {
		this.closeDialogSource.next();
	}

	updateCompleted(): void {
		this.updateCompletedSource.next();
		this.closeDialog();
	}

	getUser(): Observable<Address[]> {
		return this.auth$.pipe(
			filter(auth => !!auth),
			map(auth => ({
				auth: auth as Authentication,
			})),
			map(({ auth }) => {
				return auth?.direcciones ?? [];
			})
		);
	}

	refeshToken(): Observable<IResponseObjectSuccess<Authentication>> {
		// if(!this.getToken()) return of()

		return this.http.post<IResponseObjectSuccess<Authentication>>(
			`${environment.microSeguridades}/pos/usuario/refreshToken`,
			{},
			{
				headers: {
					authorization: `Bearer ${this.getToken()}` as string,
				},
			}
		);
	}

	find(id?: string | null): Observable<IResponseSuccess<Client>> {
		return this.store$.pipe(
			filter((store): store is StoreModel => !!store),
			filter(() => !!id),
			switchMap(store => {
				return this.http.get<IResponseSuccess<Client>>(`${environment.microSeguridades}/pos/cliente/${id}`, {
					headers: {
						authorization: store.apiKey,
					},
				});
			}),
			take(1)
		);
	}

	update(clientForm: ClientUserForm, auth: Authentication): Observable<IResponseSuccess<Client>> {
		return this.store$.pipe(
			filter((store): store is StoreModel => !!store),
			filter(() => !!clientForm && !!auth),
			switchMap(store => {
				return this.http.patch<IResponseSuccess<Client>>(
					`${environment.microSeguridades}/pos/usuario/${auth.id}`,
					transformToClientUpdate(clientForm as ClientUserForm),
					{
						headers: {
							authorization: store.apiKey,
						},
					}
				);
			}),
			take(1)
		);
	}

	authenticate(authenticationForm: AuthenticationForm): Observable<IResponseObjectSuccess<Authentication>> {
		return this.http.post<IResponseObjectSuccess<Authentication>>(`${environment.microSeguridades}/pos/usuario/login`, {
			...transformAuthUpdate(authenticationForm),
			modoCliente: true,
		});
	}

	register(authenticationForm: AuthenticationForm): Observable<IResponseObjectSuccess<Authentication>> {
		return this.store$.pipe(
			take(1),
			filter(store => !!store),
			switchMap(store => {
				return this.http.post<IResponseObjectSuccess<Authentication>>(
					`${environment.microSeguridades}/pos/cliente`,
					{
						...transformAuthUpdate(authenticationForm),
						tienda_id: store?.idLocal as string,
						estado_id: store?.estado_id as string,
						cuenta_id: store?.cuenta_id as string,
						moduloRegistro: 'JuvOnline',
					},
					{
						headers: {
							authorization: store?.apiKey as string,
						},
					}
				);
			})
		);
	}

	requestResetPassword(authenticationForm: AuthenticationForm): Observable<IResponseObjectSuccess<Authentication>> {
		return this.store$.pipe(
			take(1),
			filter(store => !!store),
			switchMap((store: StoreModel) => {
				return this.http.post<IResponseObjectSuccess<Authentication>>(
					`${environment.microSeguridades}/pos/usuario/resetPassword`,
					{ correo: authenticationForm.email },
					{
						headers: {
							authorization: store?.apiKey as string,
						},
					}
				);
			})
		);
	}

	changePasswordWithToken(nuevaContrasena: string, token: string): Observable<IResponse> {
		const url = `${environment.microSeguridades}/pos/usuario/changePassword`;
		return this.http.post<IResponse>(url, { token, contrasena: nuevaContrasena });
	}
  
  /**
   * Obtiene información de un local por su ID
   * @param localId ID del local
   * @returns Observable con los datos del local
   */
  // ? TO DO: No se usa
  getLocalById(localId: string): Observable<IResponseSuccess<StoreModel[]>> {
    return this.http
			.get<IResponseSuccess<StoreModel[]>>(`${environment.microConfiguraciones}/locales/${localId}`, {
      headers: this.getDefaultHeaders()
    }).pipe(
      catchError((error) => {
        return throwError(() => new Error('Error fetching local data'));
      })
    );
  }

	/**
	 * Obtiene la lista de estados
	 * @returns Observable con la lista de estados
	 */
	getStatuss(): Observable<IResponseSuccess<Status>> {
		return this.http
			.get<IResponseSuccess<Status>>(`${environment.microConfiguraciones}/estados`, {
				headers: this.getDefaultHeaders(),
			})
			.pipe(
				catchError(error => {
					return throwError(() => new Error('Error fetching estados'));
				})
			);
	}

	/**
	 * Cierra la sesión del usuario
	 */
	logout(): void {
		// Asegúrate de devolver un Observable
		this.cookieService.delete(this.TOKEN_KEY, '/');
		this.store.dispatch(USER_CLEAR_STATE())
		this.store.dispatch(CLIENT_CLEAR_STATE())
		// window.location.reload();
	}

	/**
	 * Verifica si el usuario está autenticado
	 * @returns boolean indicando si está autenticado
	 */
	isAuthenticated(): boolean {
		return this.isTokenValid();
	}

	getToken(): string | null {
		return this.cookieService.get(this.TOKEN_KEY) || null;
	}

	getUserData(): User | null {
		const userData = localStorage.getItem(this.USER_KEY);
		return userData ? JSON.parse(userData) : null;
	}

	/**
	 * Obtiene la tienda principal del usuario
	 * @returns Datos de la tienda o null
	 */
	getCurrentStore(): string | null {
		const storedData = localStorage.getItem('currentStore');
		if (!storedData) {
			throw new Error('storeData no encontrado en localStorage');
		}

		try {
			const storeInfo = JSON.parse(storedData);
			if (!storeInfo.idLocal) {
				throw new Error('localId no está definido en storeData');
			}
			this.idLocal = storeInfo.idLocal;
		} catch (e) {
			throw e;
		}
		return this.idLocal || null;
	}

	/**
	 * Verifica si el token es válido
	 * @returns boolean indicando si el token es válido
	 */
	isTokenValid(): boolean {
		const token = this.getToken();
		if (!token) return false;

		const expiry = this.cookieService.get(this.TOKEN_EXPIRY_KEY);
		if (!expiry) return false;

		return new Date(expiry) > new Date();
	}

	private getDefaultHeaders(): HttpHeaders {
		let headers = new HttpHeaders({
			Accept: 'application/json',
		});

		const token = this.getToken();
		if (token) {
			headers = headers.set('Authorization', `Bearer ${token}`);
		}

		return headers;
	}
}
